import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'

import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import ExperienceDetailView from './ExperienceListingDrawer/ExperienceDetailView'
import type { DrawerExperience } from './ExperienceListingDrawer/ExperienceDetailView'
import { mapExperienceDetailToDrawer } from './ExperienceListingDrawer/mapExperienceDetailToDrawer'
import useExperienceDetail from '../hooks/useExperienceDetail'
import useOfferReviews from '../hooks/useOfferReviews'
import type { TripItineraryData } from 'tripPlanner/api/types'
import { useExperience } from 'tripPlanner/hooks/api/offer'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'

const SkeletonWrapper = styled.div`
  padding: ${rem(20)};
`

const FullWidthLoadingBox = styled(LoadingBox)`
  width: 100%;
`

const NarrowLoadingBox = styled(LoadingBox)`
  width: 80%;
`

const ErrorWrapper = styled(Group)`
  padding: ${rem(48)} ${rem(24)};
  text-align: center;
`

interface Props {
  offerId: string
  isOpen: boolean
  onClose: () => void
  onAfterClose?: () => void
  onShowMap?: (experience: DrawerExperience) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string) => void
  scrollToPricing?: boolean
}

function ChatExperienceDetailDrawer({
  offerId,
  isOpen,
  onClose,
  onAfterClose,
  onShowMap,
  itineraryData,
  onSendMessage,
  scrollToPricing,
}: Props) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const currency = useContext(CurrencyContext)

  const { experienceDetail, isLoading, isError, refetch } = useExperienceDetail({
    offerId: isOpen ? offerId : undefined,
    currencyCode: currency,
    enabled: isOpen,
  })
  const standaloneOffer = useExperience({
    experienceId: isOpen ? offerId : undefined,
    currentCurrency: currency,
    currentRegionCode: geo.currentRegionCode,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: isOpen ? offerId : undefined,
    region: geo.currentRegionCode,
    enabled: isOpen && !!experienceDetail,
  })

  const drawerExperience = useMemo(() => {
    if (!experienceDetail) return undefined
    return mapExperienceDetailToDrawer(experienceDetail)
  }, [experienceDetail])

  const handleShowMap = useCallback(() => {
    if (drawerExperience) {
      onClose()
      onShowMap?.(drawerExperience)
    }
  }, [onClose, onShowMap, drawerExperience])

  const hasGeoLocation = !!drawerExperience?.geoLocation

  const headerActions = useMemo(() => {
    if (!drawerExperience) return undefined
    const showMapLabel = intl.formatMessage({
      id: 'uyDWABuVrlw97fTIbbjL',
      defaultMessage: 'Show map',
      description: 'Button label to show hotel location on map in hotel detail view',
    })
    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {hasGeoLocation && (
          <FilterChip size="small" startIcon={<LineMapIcon />} onClick={handleShowMap}>
            {showMapLabel}
          </FilterChip>
        )}
        {itineraryData && onSendMessage && (
          <AddToTripAction
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            placeName={drawerExperience.name}
            offerId={drawerExperience.offerId}
            offerType="experience"
            coordinates={drawerExperience.geoLocation}
            variant="outline"
          />
        )}
      </Group>
    )
  }, [drawerExperience, hasGeoLocation, handleShowMap, intl, itineraryData, onSendMessage])

  const failedToLoadLabel = intl.formatMessage({
    id: 'clAQJsTYp1RaYNVY1-Rz',
    defaultMessage: 'Failed to load experience details.',
    description: 'Error message shown when experience offer details fail to load in the drawer',
  })

  const retryLabel = intl.formatMessage({
    id: 'q2nIV5-Sco41UZtxNQNg',
    defaultMessage: 'Try again',
    description: 'Button label to retry loading hotel offer details after an error',
  })

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={onAfterClose}
      direction="right-to-left"
      headerActions={headerActions}
    >
      {isLoading && (
        <SkeletonWrapper>
          <Group direction="vertical" gap={16}>
            <FullWidthLoadingBox height={200} borderRadius="M" />
            <LoadingBox height={28} width={240} borderRadius="S" />
            <LoadingBox height={20} width={160} borderRadius="S" />
            <FullWidthLoadingBox height={16} borderRadius="S" />
            <NarrowLoadingBox height={16} borderRadius="S" />
          </Group>
        </SkeletonWrapper>
      )}
      {isError && !isLoading && !drawerExperience && (
        <ErrorWrapper direction="vertical" gap={8} horizontalAlign="center" verticalAlign="center">
          <BodyText variant="medium" colour="critical">
            {failedToLoadLabel}
          </BodyText>
          <TextButton kind="secondary" size="small" onClick={() => refetch()}>
            {retryLabel}
          </TextButton>
        </ErrorWrapper>
      )}
      {drawerExperience && (
        <ExperienceDetailView
          experience={drawerExperience}
          standaloneOffer={standaloneOffer.data}
          reviews={reviews}
          reviewsLoading={reviewsLoading}
          itineraryData={itineraryData}
          onSendMessage={onSendMessage}
          scrollToPricing={scrollToPricing}
        />
      )}
    </TripDrawer>
  )
}

export default ChatExperienceDetailDrawer
